/**
 * `useNotificationTapHandlers`
 *
 * Binds the three Flutter notification tap handlers
 * (`messages_screen.dart:629-673`) to React, so `InboxPage` stays a thin page
 * shell (component-size threshold, `presentation.md` → "📐 Component size").
 *
 * This is a component/surface-scoped hook — it lives beside the tap modules
 * under `notificationTap/`, NOT at `feature/inbox/hooks/use[Feature].ts`, and is
 * not the inbox feature hook. The ban on `Navigator.*` / sheets binds the page
 * feature hook (`useInbox`, untouched here); surface hooks legitimately own the
 * concrete navigation/sheet surface, per the `useContactSheetSurface` /
 * `useAddRoadmapSheetSurface` precedents.
 *
 * Layer boundary: this hook invokes NO use case and holds NO `read` predicate.
 * Every piece of inbox/auth state it needs — `markNotificationRead` (the `!read`
 * gate, the Firestore write and the flip-on-success-only update) and
 * `refreshCurrentUser` — is owned by the inbox feature hook and reaches here as a
 * parameter that `InboxPage` reads off `useInboxContext()`.
 */
import { useCallback } from 'react';
import { Navigator } from '@/presentation/routing/Navigator';
import { useDateFormatters } from '@/presentation/utils/useDateFormatters';
import type { AppNotificationEntity } from '@/domain/entities/notification/appNotificationEntity';
import type { l10n } from '@/presentation/locale/l10n';
import { dispatchNotificationTap } from './notificationTapRouter';

export interface UseNotificationTapHandlersParams {
  /** Owning route's screen key, for sheet containment (page-minted). */
  screenKey: string;
  /** Locale bundle — threaded into the dispatcher context for sheet copy. */
  l10n: l10n;
  /** From `useInboxContext()`: gated read write + optimistic flip (business logic). */
  markNotificationRead: (notification: AppNotificationEntity) => Promise<void>;
  /** From `useInboxContext()`: Flutter `UsersRepository.refreshUser()`. */
  refreshCurrentUser: () => Promise<void>;
}

export interface UseNotificationTapHandlersResult {
  onNotificationItemTap: (notification: AppNotificationEntity) => void;
  onNotificationUserTap: (userId: string) => void;
  onNotificationMediaTap: (notification: AppNotificationEntity) => void;
}

export const useNotificationTapHandlers = ({
  screenKey,
  l10n,
  markNotificationRead,
  refreshCurrentUser,
}: UseNotificationTapHandlersParams): UseNotificationTapHandlersResult => {
  // Handlers under `notificationTap/` are plain functions, not hooks, so the
  // formatter is read here and threaded into their context.
  const { formatDate } = useDateFormatters();

  // Whole-row notification tap — `_onNotificationItemTap`
  // (`messages_screen.dart:659-673`). Dispatch FIRST (the dispatcher is
  // synchronous, so it completes here), THEN hand off to the hook, which owns
  // the `!read` gate, the awaited Firestore write and the flip-on-success-only
  // update. Flutter's badge re-check (`:673`) needs no code — `hasUnseenInbox`
  // re-derives from the flipped list.
  const onNotificationItemTap = useCallback(
    (notification: AppNotificationEntity) => {
      dispatchNotificationTap({
        notification,
        screenKey,
        l10n,
        formatDate,
        refreshCurrentUser,
      });
      void markNotificationRead(notification);
    },
    [screenKey, l10n, formatDate, refreshCurrentUser, markNotificationRead],
  );

  // Leading-avatar tap — `_onNotificationItemUserTap`
  // (`messages_screen.dart:655-657`). Pure navigation: no per-type switch and
  // no mark-read, so the row stays unread.
  const onNotificationUserTap = useCallback((userId: string) => {
    Navigator.navigateToUserDetails(userId);
  }, []);

  // Trailing-thumbnail tap — `_onNotificationItemMediaTap`
  // (`messages_screen.dart:629-653`). Flutter builds a one-item `UserMedia` and
  // pushes the pager with `fetchSingleMediaPath: media.relativePath` and NO
  // sheet flags; the web equivalent is a by-path navigation with no options
  // (the same call `CommentMediaThumbnail.tsx:61` makes). The `?.` guard
  // replaces Flutter's `mediaInfo!` null-assert. No mark-read here either.
  const onNotificationMediaTap = useCallback((notification: AppNotificationEntity) => {
    const path = notification.info.mediaInfo?.path;
    if (!path) return;
    Navigator.navigateToContentDetailsByPath(path);
  }, []);

  return { onNotificationItemTap, onNotificationUserTap, onNotificationMediaTap };
};
